import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Shield, Users, Sparkles, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/config/brand";
import { useAuth } from "@/context/AuthContext";
import WaitlistCard from "@/components/WaitlistCard";
import { saveIntent } from "@/lib/intent";

const PERSONAS = [
  { name: "Riya", age: 22, vibe: "Playful college student. Music, memes and late-night talks.", line: "Tell me the song stuck in your head today.", img: "https://res.cloudinary.com/ro2tyrzq/image/upload/v1791197606/w_400%2Cq_auto%2Cf_auto/Riya.png" },
  { name: "Kabir", age: 24, vibe: "Cricket and gaming buddy. Easy, funny, always up for banter.", line: "Who is your all-time favourite cricketer?", img: "https://res.cloudinary.com/ro2tyrzq/image/upload/v1791197605/w_400%2Cq_auto%2Cf_auto/Kabir.png" },
  { name: "Meera", age: 28, vibe: "Calm listener. Journaling, chai and quiet evenings.", line: "Rough day or good day? I have time.", img: "https://res.cloudinary.com/ro2tyrzq/image/upload/v1791197606/w_400%2Cq_auto%2Cf_auto/Meera.png" },
  { name: "Arjun", age: 29, vibe: "Startup-minded. Good for career talk.", line: "What is the one thing you would change at work?", img: "https://res.cloudinary.com/ro2tyrzq/image/upload/v1791195490/w_400%2Cq_auto%2Cf_auto/Arjun.png" },
  { name: "Naina", age: 32, vibe: "Foodie and traveller.", line: "Best meal you have ever had. Go.", img: "https://res.cloudinary.com/ro2tyrzq/image/upload/v1791197606/w_400%2Cq_auto%2Cf_auto/Naina.png" },
  { name: "Dev", age: 35, vibe: "Dad-joke philosopher. Easy to talk to about anything.", line: "Ask me anything. I will answer badly but sincerely.", img: "https://res.cloudinary.com/ro2tyrzq/image/upload/v1791197605/w_400%2Cq_auto%2Cf_auto/Dev.png" },
  { name: "Sana", age: 38, vibe: "Practical and warm. Wellness, family and everyday life.", line: "What is taking up your headspace this week?", img: "https://res.cloudinary.com/ro2tyrzq/image/upload/v1791197606/w_400%2Cq_auto%2Cf_auto/Sana.png" },
  { name: "Harsh", age: 41, vibe: "Reflective and steady. Books and life lessons.", line: "What book changed how you think?", img: "https://res.cloudinary.com/ro2tyrzq/image/upload/v1791197605/w_400%2Cq_auto%2Cf_auto/Harsh.png" },
  { name: "Kavita", age: 43, vibe: "Warm Gujarati speaker. Family, food and culture.", line: "Let’s be honest with eachother.", img: "https://res.cloudinary.com/ro2tyrzq/image/upload/v1791197605/w_400%2Cq_auto%2Cf_auto/Kavita.png" },
  { name: "Vikram", age: 45, vibe: "Storyteller with a sense of humour. Calm, unhurried.", line: "Want a story or a problem solved?", img: "https://res.cloudinary.com/ro2tyrzq/image/upload/v1791197607/w_400%2Cq_auto%2Cf_auto/Vikram.png" },
];

function PersonaCard({ persona, onClick }) {
  const [imgFailed, setImgFailed] = useState(false);
  const alt = `${persona.name}, AI persona (AI-generated image)`;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`persona-card-${persona.name.toLowerCase()}`}
      className="flex min-h-[44px] flex-col items-center rounded-2xl border border-[#2B241B] bg-[#12100E] p-4 text-center transition-colors hover:border-[#D4AF37] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]"
    >
      <span className="relative inline-block">
        {imgFailed ? (
          <span role="img" aria-label={alt} className="flex h-24 w-24 items-center justify-center rounded-full border border-[#3D3428] bg-[#242220] font-display text-2xl text-[#D4AF37]">
            {persona.name[0]}
          </span>
        ) : (
          <img
            src={persona.img}
            alt={alt}
            width={96}
            height={96}
            loading="lazy"
            onError={() => setImgFailed(true)}
            className="h-24 w-24 rounded-full border border-[#3D3428] object-cover"
          />
        )}
        <span className="absolute -right-1 -top-1 rounded-full bg-[#D4AF37] px-1.5 py-0.5 text-[10px] font-bold text-[#0A0908]">AI</span>
      </span>
      <span className="mt-3 text-sm font-semibold text-[#F4F1EA]">{persona.name}, {persona.age}</span>
      <span className="mt-1 text-xs leading-snug text-[#C8C2B5]">{persona.vibe}</span>
      <span className="mt-2 text-xs italic leading-snug text-[#D4AF37]">“{persona.line}”</span>
    </button>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const start = () => navigate("/login");
  const { user } = useAuth();
  // Landing cards remember the visitor's choice; after login AppShell routes them
  // straight into it. Already-signed-in visitors skip the login step entirely.
  const choose = (type) => {
    if (user) { navigate(type === "human" ? "/app/match" : "/app/discover"); return; }
    saveIntent(type);
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0A0908] bg-noise text-[#F4F1EA]">
      {/* 1. Nav */}
      <header className="mx-auto flex max-w-6xl items-center justify-between px-5 py-6">
        <Link to="/" className="flex items-center gap-2 no-underline">
          <img src="https://res.cloudinary.com/ro2tyrzq/image/upload/v1791541018/w_400%2Cq_auto%2Cf_auto/maziara.png" alt="Maziara logo" className="h-8 w-auto md:h-10" />
          <span className="font-display text-2xl text-gold-gradient">{BRAND.name}</span>
        </Link>
        <div className="flex items-center gap-4 text-sm text-[#C8C2B5]">
          <Link to="/safety" className="hover:text-[#D4AF37]" data-testid="landing-safety-link">Safety</Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5">
        {/* 2. Hero */}
        <section className="grid items-center gap-10 py-12 md:grid-cols-2 md:py-20">
          <div className="text-center md:text-left">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#3D3428] px-3 py-1 text-xs uppercase tracking-[0.2em] text-[#D4AF37]" data-testid="landing-eligibility-pill">
              <Sparkles className="h-3 w-3" /> For people aged 18+ in India
            </div>
            <h1 className="mt-4 font-display text-4xl font-medium leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              A place to talk without performing.
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-[#C8C2B5] md:mx-0">
              Everyone is posting. Almost no one is listening. Skip the feed and chat one-to-one with a real person, or choose an AI persona, without likes, follower counts or public profiles.
            </p>
            <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row md:justify-start sm:justify-center">
              <Button data-testid="landing-start-conversation" onClick={start} className="rounded-full bg-[#D4AF37] px-8 py-6 text-base font-semibold text-[#0A0908] hover:bg-[#E5C158]">
                Start a conversation
              </Button>
            </div>
            <p className="mt-4 text-sm text-[#C8C2B5]">Free preview. No credit card required.</p>
          </div>

          <div className="rounded-2xl border border-[#2B241B] bg-[#12100E] p-6" data-testid="landing-sample-chat">
            <div className="space-y-3">
              <div className="max-w-[85%] rounded-2xl rounded-bl-sm border border-[#3D3428] bg-[#242220] px-4 py-3 text-sm leading-relaxed text-[#F4F1EA]">
                Rough day. I didn't want to post about it.
              </div>
              <div className="ml-auto max-w-[85%] rounded-2xl rounded-br-sm bg-[#D4AF37]/20 px-4 py-3 text-sm leading-relaxed text-[#E5C158]">
                Then don't. Tell me instead.
              </div>
              <div className="max-w-[85%] rounded-2xl rounded-bl-sm border border-[#3D3428] bg-[#242220] px-4 py-3 text-sm leading-relaxed text-[#F4F1EA]">
                It's strange. I just want someone to hear it.
              </div>
            </div>
            <p className="mt-5 text-center text-sm text-[#C8C2B5]">A sample conversation. No photos, no usernames, no like button.</p>
          </div>
        </section>

        {/* 3. Dark highlighted block */}
        <section className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#1a1509] to-[#12100E] p-8" data-testid="landing-differentiator">
          <h2 className="font-display text-2xl text-[#F4F1EA]">Not another chat-with-strangers site.</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#C8C2B5]">
            Open rooms and throwaway accounts attract the wrong crowd. Maziara needs a login, runs on a paid membership, and keeps end, block and report within reach in every chat.
          </p>
        </section>

        {/* 4. Choose who you talk to */}
        <section className="mt-14" data-testid="landing-modes">
          <h2 className="text-center font-display text-3xl text-[#F4F1EA]">Choose who you talk to.</h2>
          <div className="mt-8">
            <div
              className="cursor-pointer rounded-2xl border border-[#2B241B] bg-[#12100E] p-6 transition-colors hover:border-[#D4AF37] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]"
              data-testid="landing-mode-real"
              role="button"
              tabIndex={0}
              onClick={() => choose("human")}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") choose("human"); }}
            >
              <Users className="mb-4 h-7 w-7 text-[#D4AF37]" />
              <h3 className="font-display text-xl text-[#F4F1EA]">Talk with a real person</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#C8C2B5]">
                Have an anonymous text conversation with another active Maziara user who shares your language. Matching depends on availability. There are no public profiles or photos.
              </p>
              <p className="mt-3 text-xs text-[#8C8578]">If no one is available right now, you can talk with an AI persona instead.</p>
            </div>

            <div className="mt-5 rounded-2xl border border-[#2B241B] bg-[#12100E] p-6" data-testid="landing-mode-ai">
              <Sparkles className="mb-4 h-7 w-7 text-[#D4AF37]" />
              <h3 className="font-display text-xl text-[#F4F1EA]">Talk with an AI persona</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#C8C2B5]">
                Talk with a fictional character that is clearly labelled as AI.
              </p>
              <p className="mt-5 text-xs uppercase tracking-[0.2em] text-[#8C8578]">Examples of AI personas you may talk to.</p>
              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="persona-grid">
                {PERSONAS.map((p) => (
                  <PersonaCard key={p.name} persona={p} onClick={() => choose("ai")} />
                ))}
              </div>
              <p className="mt-5 text-xs text-[#8C8578]">Always labelled. Never passed off as a person.</p>
            </div>
          </div>
        </section>

        {/* 5. No feed. No audience. */}
        <section className="my-14 text-center" data-testid="landing-no-feed">
          <h2 className="font-display text-3xl text-[#F4F1EA]">No feed. No audience.</h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-[#C8C2B5]">
            Maziara is designed for one-to-one conversation, without public profiles, followers, likes or popularity counts. There is no audience to impress, just a conversation you choose.
          </p>
        </section>

        {/* 6. How it works — section removed per request */}

        {/* 7. Boundaries */}
        <section className="my-14 text-center" data-testid="landing-boundaries">
          <h2 className="font-display text-3xl text-[#F4F1EA]">Your boundaries matter.</h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-[#C8C2B5]">
            End, block or report a chat at any time. Maziara provides safety controls and{" "}
            <Link to="/safety" className="text-[#D4AF37] underline decoration-[#3D3428] underline-offset-2 hover:text-[#E5C158]">links to independent support resources</Link>.
            It is not a crisis service.
          </p>
        </section>

        {/* 8. Complimentary early access + membership waitlist */}
        <section className="my-14" data-testid="landing-early-access">
          <div className="rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#1a1509] to-[#12100E] p-8 text-center">
            <Gift className="mx-auto mb-4 h-7 w-7 text-[#D4AF37]" />
            <h2 className="font-display text-3xl text-[#F4F1EA]">Complimentary Early Access</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-[#C8C2B5]">
              Enjoy 20 AI messages and 10 new connections each day, on us, during Maziara's early-access period.
            </p>
          </div>

          <div className="mx-auto mt-6 max-w-md rounded-2xl border border-[#2B241B] bg-[#12100E] p-6 text-center" data-testid="landing-membership-waitlist">
            <h3 className="font-display text-2xl text-[#F4F1EA]">A more personal circle.</h3>
            <p className="mt-2 text-sm leading-relaxed text-[#C8C2B5]">
              Join the private membership waitlist for priority consideration when Maziara membership opens.
            </p>
            <WaitlistCard joined={false} />
            <p className="mt-3 text-xs text-[#8C8578]">Early access is complimentary. Future membership invitations will be released in stages.</p>
          </div>
        </section>

        {/* 9. Founder note */}
        <section className="my-14" data-testid="landing-founder-note">
          <div className="mx-auto max-w-2xl rounded-2xl border border-[#2B241B] bg-[#12100E] p-8 text-center">
            <Shield className="mx-auto mb-4 h-6 w-6 text-[#D4AF37]" />
            <blockquote className="font-display text-xl leading-relaxed text-[#F4F1EA]">
              “I built Maziara because a feed can be full of people and still leave you with no one to talk to. I wanted a quieter place where nobody is keeping score.”
            </blockquote>
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-[#8C8578]">Founder</p>
          </div>
        </section>

        {/* 10. Final CTA */}
        <section className="my-14 rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#1a1509] to-[#12100E] p-8 text-center md:p-12" data-testid="landing-final-cta">
          <h2 className="font-display text-3xl text-[#F4F1EA]">You don't have to be impressive here.</h2>
          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-[#C8C2B5]">
            Try one conversation free. If it isn't for you, there is nothing to cancel.
          </p>
          <Button data-testid="landing-final-start" onClick={start} className="mt-6 rounded-full bg-[#D4AF37] px-8 py-6 text-base font-semibold text-[#0A0908] hover:bg-[#E5C158]">
            Start a conversation
          </Button>
        </section>
      </main>

      {/* 11. Footer — unchanged */}
      <footer className="border-t border-[#2B241B] py-8">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-4 px-5 text-sm text-[#8C8578]">
          <Link to="/how-it-works" className="hover:text-[#D4AF37]">How It Works</Link>
          <Link to="/safety" className="hover:text-[#D4AF37]">Safety</Link>
          <Link to="/guidelines" className="hover:text-[#D4AF37]">Guidelines</Link>
          <Link to="/privacy" className="hover:text-[#D4AF37]">Privacy</Link>
          <Link to="/terms" className="hover:text-[#D4AF37]">Terms</Link>
          <Link to="/ai-disclosure" className="hover:text-[#D4AF37]">AI Disclosure</Link>
          <Link to="/refund" className="hover:text-[#D4AF37]">Refunds</Link>
          <Link to="/account-deletion" className="hover:text-[#D4AF37]">Delete Account</Link>
          <Link to="/contact" className="hover:text-[#D4AF37]">Contact</Link>
          <Link to="/blog" className="hover:text-[#D4AF37]" data-testid="landing-blog-link">Blog</Link>
        </div>
        <p className="mt-4 text-center text-xs text-[#5f594d]">{BRAND.name} · {BRAND.domain}</p>
      </footer>
    </div>
  );
}
